export const dashboardUserAccessFeatures = [
  { value: "playbrain-website", label: "Playbrain Website" },
  { value: "contract-maker", label: "Contract Maker" },
  { value: "temp-page", label: "Temp Page" },
  { value: "goal-setting", label: "Goal Setting" },
  { value: "user-management", label: "User Management and Roles" },
  { value: "taiyoro-cms", label: "Taiyoro CMS" },
] as const

export const dashboardUserAccessLevels = [
  { value: "viewer", label: "Viewer" },
  { value: "editor", label: "Editor" },
  { value: "admin", label: "Admin" },
] as const

export type DashboardUserAccessFeature = (typeof dashboardUserAccessFeatures)[number]["value"]
export type DashboardUserAccessLevel = (typeof dashboardUserAccessLevels)[number]["value"]

export type DashboardUserAccessGrant = {
  feature: DashboardUserAccessFeature
  /**
   * The key of an access level role. Once roles became user-defined this stopped
   * being one of three fixed levels, so it cannot be checked against a list —
   * only that it is a non-empty string.
   */
  access: string
}

export function isDashboardUserAccessFeature(value: unknown): value is DashboardUserAccessFeature {
  return dashboardUserAccessFeatures.some((feature) => feature.value === value)
}

export function isDashboardUserAccessLevel(value: unknown): value is DashboardUserAccessLevel {
  return dashboardUserAccessLevels.some((level) => level.value === value)
}

/**
 * Deduplicates grants while keeping every distinct role on a feature: a user may
 * hold several, and the permissions they grant are unioned server-side.
 */
export function normalizeDashboardUserAccessGrants(
  grants: readonly { feature: unknown; access: unknown }[] = []
): DashboardUserAccessGrant[] {
  const seen = new Set<string>()
  const next: DashboardUserAccessGrant[] = []
  for (const grant of grants) {
    if (!isDashboardUserAccessFeature(grant.feature)) continue
    if (typeof grant.access !== "string" || !grant.access.trim()) continue
    const signature = `${grant.feature}:${grant.access}`
    if (seen.has(signature)) continue
    seen.add(signature)
    next.push({ feature: grant.feature, access: grant.access })
  }
  return next
}

export function upsertDashboardUserAccessGrant(
  grants: readonly DashboardUserAccessGrant[],
  grant: DashboardUserAccessGrant
): DashboardUserAccessGrant[] {
  return normalizeDashboardUserAccessGrants([...grants, grant])
}

export function dashboardUserAccessFeatureLabel(feature: DashboardUserAccessFeature) {
  return dashboardUserAccessFeatures.find((option) => option.value === feature)?.label ?? feature
}

export function dashboardUserAccessLevelLabel(access: DashboardUserAccessLevel) {
  return dashboardUserAccessLevels.find((option) => option.value === access)?.label ?? access
}
